/* ============================================================
   MOONRISE — SHOPIFY THEME
   theme.js — global behaviors only.
   Section-scoped behaviors (sliders, carousels, counters)
   live inside each section's <script> block and are scoped
   by data-section-id for Shopify Theme Editor compatibility.
   ============================================================ */

'use strict';

(function () {

  /* --------------------------------------------------------
     Bootstrapping
     -------------------------------------------------------- */
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    initAnnouncementBar();
    initAnnouncementRotator();
    initStickyHeader();
    initMobileNav();
    initSiteDrawers();
    initBackToTop();
    initScrollAnimations();
    initSmoothScroll();
    initLazyMedia();
    initInstafeed();
    initShareCopy();
  });

  // Re-init on Shopify Theme Editor section reload — keep global handlers fresh.
  document.addEventListener('shopify:section:load', function () {
    initStickyHeader();
    initMobileNav();
    initSiteDrawers();
    initAnnouncementBar();
    initAnnouncementRotator();
    initScrollAnimations();
  });


  /* --------------------------------------------------------
     1. Announcement bar (dismiss + sessionStorage)
     -------------------------------------------------------- */
  function initAnnouncementBar() {
    var bar = document.getElementById('announcementBar');
    var close = document.getElementById('announcementClose');
    if (!bar) return;

    if (sessionStorage.getItem('announcementDismissed') === 'true') {
      bar.classList.add('is-hidden');
      return;
    }

    if (close && !close.dataset.bound) {
      close.dataset.bound = '1';
      close.addEventListener('click', function () {
        bar.classList.add('is-hidden');
        sessionStorage.setItem('announcementDismissed', 'true');
      });
    }
  }


  /* --------------------------------------------------------
     1b. Announcement rotator (mobile only — rotates ticker items every 3s)
     -------------------------------------------------------- */
  function initAnnouncementRotator() {
    var items = document.querySelectorAll('.announcement-ticker-wrap .ticker-item');
    if (!items.length) return;

    function apply() {
      var isMobile = window.matchMedia('(max-width: 768px)').matches;
      items.forEach(function (el, idx) {
        el.classList.toggle('is-active', !isMobile || idx === 0);
      });
    }
    apply();
    window.addEventListener('resize', apply);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var liveEl = document.getElementById('announcementLive');
    var i = 0;
    setInterval(function () {
      if (!window.matchMedia('(max-width: 768px)').matches) return;
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
      if (liveEl) liveEl.textContent = items[i].textContent.trim();
    }, 3000);
  }


  /* --------------------------------------------------------
     2. Sticky header — adds .is-scrolled past 80px
     -------------------------------------------------------- */
  function initStickyHeader() {
    var header = document.getElementById('siteHeader');
    if (!header) return;

    // If sticky is disabled via the Theme Editor setting, clear any shadow and bail
    if (header.dataset.sticky === 'false') {
      header.classList.remove('is-scrolled');
      window.removeEventListener('scroll', header._mrOnScroll || function () {});
      return;
    }

    var ticking = false;
    function update() {
      header.classList.toggle('is-scrolled', window.scrollY > 80);
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      window.requestAnimationFrame(update);
      ticking = true;
    }

    window.removeEventListener('scroll', header._mrOnScroll || function () {});
    header._mrOnScroll = onScroll;
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }


  /* --------------------------------------------------------
     3. Mobile navigation + submenu toggles
     -------------------------------------------------------- */
  function initMobileNav() {
    var menuBtn = document.getElementById('mobileMenuBtn');
    var closeBtn = document.getElementById('mobileNavClose');
    var overlay = document.getElementById('mobileNavOverlay');
    var backdrop = document.getElementById('mobileNavBackdrop');
    if (!menuBtn || !overlay) return;

    function openNav(e) {
      overlay.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-visible');
      document.body.classList.add('nav-open');
      menuBtn.setAttribute('aria-expanded', 'true');
      /* Prefer the close button so the first Tab stays inside the panel. */
      window.Moonrise.overlay.open(overlay, (e && e.currentTarget) || menuBtn, '#mobileNavClose');
    }
    function closeNav() {
      overlay.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-visible');
      document.body.classList.remove('nav-open');
      menuBtn.setAttribute('aria-expanded', 'false');
      window.Moonrise.overlay.close(overlay);
    }

    if (!menuBtn.dataset.bound) {
      menuBtn.dataset.bound = '1';
      menuBtn.addEventListener('click', openNav);
    }
    if (closeBtn && !closeBtn.dataset.bound) {
      closeBtn.dataset.bound = '1';
      closeBtn.addEventListener('click', closeNav);
    }
    if (backdrop && !backdrop.dataset.bound) {
      backdrop.dataset.bound = '1';
      backdrop.addEventListener('click', closeNav);
    }

    if (!document._mrEscBound) {
      document._mrEscBound = true;
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        if (overlay.classList.contains('is-open')) closeNav();
      });
    }

    var toggleBtns = overlay.querySelectorAll('.mobile-nav-toggle');
    toggleBtns.forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function () {
        var parentItem = this.closest('.mobile-nav-item');
        var subList = parentItem ? parentItem.querySelector('.mobile-sub-list') : null;
        var isOpen = this.classList.contains('is-active');

        toggleBtns.forEach(function (other) {
          other.classList.remove('is-active');
          other.setAttribute('aria-expanded', 'false');
          var otherItem = other.closest('.mobile-nav-item');
          var otherSub = otherItem ? otherItem.querySelector('.mobile-sub-list') : null;
          if (otherSub) otherSub.classList.remove('is-open');
        });

        if (!isOpen && subList) {
          this.classList.add('is-active');
          this.setAttribute('aria-expanded', 'true');
          subList.classList.add('is-open');
        }
      });
    });
  }


  /* --------------------------------------------------------
     4. Search + Cart drawers (shared factory)
     -------------------------------------------------------- */
  function initDrawer(opts) {
    var triggers = opts.triggers;
    var drawer = opts.drawer;
    var backdrop = opts.backdrop;
    var closeBtn = opts.closeBtn;
    if (!triggers || !triggers.length || !drawer || !backdrop) return;

    var lastTrigger = null;

    function open(e) {
      if (e) e.preventDefault();
      lastTrigger = (e && (e.currentTarget || e.target)) || null;
      drawer.classList.add('is-open');
      backdrop.classList.add('is-visible');
      document.body.classList.add('drawer-open');
      /* The search field is the right landing spot here, not the close button. */
      window.Moonrise.overlay.open(drawer, lastTrigger, 'input[type="search"], input[name="q"], input');
    }
    function close() {
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-visible');
      document.body.classList.remove('drawer-open');
      window.Moonrise.overlay.close(drawer);
      lastTrigger = null;
    }

    triggers.forEach(function (t) {
      if (t.dataset.bound) return;
      t.dataset.bound = '1';
      t.addEventListener('click', open);
    });
    if (!backdrop.dataset.bound) {
      backdrop.dataset.bound = '1';
      backdrop.addEventListener('click', close);
    }
    if (closeBtn && !closeBtn.dataset.bound) {
      closeBtn.dataset.bound = '1';
      closeBtn.addEventListener('click', close);
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  function initSiteDrawers() {
    initDrawer({
      triggers: document.querySelectorAll('.util-search, [data-drawer-trigger="search"]'),
      drawer: document.getElementById('searchDrawer'),
      backdrop: document.getElementById('searchBackdrop'),
      closeBtn: document.getElementById('searchClose')
    });
    // Cart drawer is owned by the <cart-drawer> custom element (assets/cart-drawer.js)
    // which binds its own open/close. Don't double-bind here.
  }


  /* --------------------------------------------------------
     5. Back-to-top button
     -------------------------------------------------------- */
  function initBackToTop() {
    var btn = document.getElementById('backToTop');
    if (!btn) return;

    var ticking = false;
    function update() {
      btn.classList.toggle('is-visible', window.scrollY > 400);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      window.requestAnimationFrame(update);
      ticking = true;
    }, { passive: true });

    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    update();
  }


  /* --------------------------------------------------------
     6. Scroll fade-in (IntersectionObserver)
     -------------------------------------------------------- */
  function initScrollAnimations() {
    var selectors = [
      '.product-card',
      '.store-service-card',
      '.review-card',
      '.fish-feature',
      '.trust-bar-item',
      '.press-logo-item',
      '.press-badge-item',
      '.instagram-item',
      '.section-header',
      '.fish-leather-inner'
    ];

    var elements = document.querySelectorAll(selectors.join(', '));
    if (!elements.length) return;

    elements.forEach(function (el, index) {
      if (el.classList.contains('anim-ready')) return;
      el.classList.add('anim-ready');
      var delay = (index % 6);
      if (delay > 0) el.classList.add('anim-delay-' + Math.min(delay, 5));
    });

    if (!('IntersectionObserver' in window)) {
      elements.forEach(function (el) { el.classList.add('anim-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('anim-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    elements.forEach(function (el) { observer.observe(el); });
  }


  /* --------------------------------------------------------
     7. Smooth scroll for in-page #anchor links
     -------------------------------------------------------- */
  function initSmoothScroll() {
    if (document._mrSmoothScrollBound) return;
    document._mrSmoothScrollBound = true;

    document.addEventListener('click', function (e) {
      var target = e.target.closest('a[href^="#"]');
      if (!target) return;
      // A link that opens a modal keeps its href as a no-JS fallback, so it
      // still matches here. Scrolling to that hash as well would slide the
      // page out from behind the dialog. Let the modal handle the click.
      if (target.hasAttribute('data-open-modal')) return;
      var href = target.getAttribute('href');
      if (!href || href === '#') return;
      var dest = document.querySelector(href);
      if (!dest) return;

      e.preventDefault();
      var header = document.getElementById('siteHeader');
      var headerH = header ? header.offsetHeight : 0;
      var top = dest.getBoundingClientRect().top + window.scrollY - headerH - 16;
      window.scrollTo({ top: top, behavior: 'smooth' });
      // Skip link: preventDefault above also stops the browser moving focus,
      // so move it into <main> (tabindex="-1") ourselves. Other anchors keep
      // their scroll-only behaviour.
      if (target.classList.contains('skip-to-content') && typeof dest.focus === 'function') {
        dest.focus({ preventScroll: true });
      }
    });
  }


  /* --------------------------------------------------------
     Share: copy link. a[data-share-copy] (product share row)
     copies its href to the clipboard instead of navigating, then
     shows "Link copied" (CSS .is-copied) and announces it via the
     row's [data-share-status]. If copying fails the link opens
     normally, which is what it did before.
     -------------------------------------------------------- */
  function initShareCopy() {
    if (document._mrShareCopyBound) return;
    document._mrShareCopyBound = true;

    function legacyCopy(text) {
      var field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      document.body.removeChild(field);
      return ok;
    }

    function copy(text) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text).catch(function () {
          if (!legacyCopy(text)) throw new Error('copy failed');
        });
      }
      return legacyCopy(text) ? Promise.resolve() : Promise.reject(new Error('copy failed'));
    }

    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[data-share-copy]');
      if (!link) return;
      e.preventDefault();
      var url = link.href;
      var label = link.getAttribute('data-copied-label') || 'Link copied';
      var status = link.parentNode && link.parentNode.querySelector('[data-share-status]');

      copy(url).then(function () {
        link.classList.add('is-copied');
        if (status) status.textContent = label;
        clearTimeout(link._mrCopiedTimer);
        link._mrCopiedTimer = setTimeout(function () {
          link.classList.remove('is-copied');
          if (status) status.textContent = '';
        }, 2000);
      }).catch(function () {
        window.location.href = url;
      });
    });
  }


  /* --------------------------------------------------------
     8. Deferred media loading (all video sections + header menu image)
     On the storefront, sections render the media URL into data-src
     instead of src:
       <video data-src preload="none">              MP4 file
       <video preload="none"><source data-src>      about video, video_tag
       <iframe data-src>                            YouTube / Vimeo
       <img data-src data-srcset>                   header dropdown image,
                                                    "Our Most-Loved Pieces" cards,
                                                    store banner, service cards
     Nothing downloads until the visitor first interacts with the page
     (pointer, touch, key, scroll, wheel) AND the element is within
     200px of the viewport; the poster shows until then. Page-speed
     tools never interact, so videos stay out of the initial load.
     Media inside a [data-lazy-group] container (the product grid,
     a sideways slider on mobile) load together once the container
     is within 200px, not one by one as each card slides in.
     The theme editor renders src directly, so previews are unchanged.
     Fires "moonrise:media-loaded" on the element once src is set, for
     scripts that need the real player (the Vimeo sound toggles).
     -------------------------------------------------------- */
  function initLazyMedia() {
    var found = document.querySelectorAll('video[data-src], video > source[data-src], iframe[data-src], img[data-src]');
    if (!found.length || document._mrLazyMediaBound) return;
    document._mrLazyMediaBound = true;

    var events = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'scroll', 'wheel'];
    var opts = { passive: true, capture: true };
    var interacted = false;
    var queue = [];
    var observer = null;

    function load(el) {
      if (el._mrMediaLoaded) return;
      el._mrMediaLoaded = true;
      var isVideo = el.tagName === 'VIDEO';

      if (isVideo) {
        // Put back the section's own preload (the about video uses
        // "metadata"); otherwise the browser default, as before deferral.
        var preload = el.getAttribute('data-preload');
        if (preload) el.setAttribute('preload', preload);
        else el.removeAttribute('preload');
      }
      if (el.hasAttribute('data-srcset')) {
        el.setAttribute('srcset', el.getAttribute('data-srcset'));
        el.removeAttribute('data-srcset');
      }
      if (el.hasAttribute('data-src')) {
        el.setAttribute('src', el.getAttribute('data-src'));
        el.removeAttribute('data-src');
      }
      var sources = el.querySelectorAll('source[data-src]');
      for (var i = 0; i < sources.length; i++) {
        sources[i].setAttribute('src', sources[i].getAttribute('data-src'));
        sources[i].removeAttribute('data-src');
      }
      if (isVideo) {
        el.load();
        if (el.hasAttribute('autoplay')) {
          var playing = el.play();
          if (playing && playing.catch) playing.catch(function () {});
        }
      }
      el.dispatchEvent(new CustomEvent('moonrise:media-loaded', { bubbles: true }));
    }

    function tryLoad(el) {
      if (interacted && el._mrMediaNear) load(el);
    }

    function onInteract() {
      if (interacted) return;
      interacted = true;
      events.forEach(function (name) { window.removeEventListener(name, onInteract, opts); });
      queue.forEach(tryLoad);
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          // A group container stands in for all the media inside it.
          var members = entry.target._mrGroupMembers || [entry.target];
          members.forEach(function (el) {
            el._mrMediaNear = true;
            tryLoad(el);
          });
        });
      }, { rootMargin: '200px 0px' });
    }

    for (var i = 0; i < found.length; i++) {
      var el = found[i].tagName === 'SOURCE' ? found[i].parentNode : found[i];
      if (el._mrMediaBound) continue;
      el._mrMediaBound = true;
      queue.push(el);
      var group = el.closest('[data-lazy-group]');
      if (!observer) {
        el._mrMediaNear = true;
      } else if (group) {
        if (!group._mrGroupMembers) {
          group._mrGroupMembers = [];
          observer.observe(group);
        }
        group._mrGroupMembers.push(el);
      } else {
        observer.observe(el);
      }
    }

    events.forEach(function (name) { window.addEventListener(name, onInteract, opts); });
  }


  /* --------------------------------------------------------
     8b. Deferred Instafeed (Instafeed app block, homepage)
     The block normally adds the Instafeed script as soon as the page
     is parsed, and that script fetches the feed, adds its CSS and
     builds the grid straight away. layout/theme.liquid sets the
     block's own "already scheduled" flag on the storefront, so the
     block skips that; this adds the same script after the visitor
     first interacts (same events as the media above) AND the feed is
     within one screen of the viewport. Page-speed tools never
     interact, so the feed stays out of the initial load.
     Theme editor: the flag is not set, the block loads as before and
     this does nothing.
     -------------------------------------------------------- */
  function initInstafeed() {
    if (!window._mrInstafeedDeferred || document._mrInstafeedBound) return;
    var feed = document.querySelector('#insta-feed, .instafeed-shopify');
    if (!feed) return;
    var src = instafeedSrc(feed);
    if (!src) return;
    document._mrInstafeedBound = true;

    var events = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'scroll', 'wheel'];
    var opts = { passive: true, capture: true };
    var interacted = false;
    var near = !('IntersectionObserver' in window);
    var loaded = false;

    function load() {
      if (loaded || !interacted || !near) return;
      loaded = true;
      // Same duplicate check as the block's loader: skip if the script
      // is already on the page (e.g. the block stops honouring the flag).
      var base = src.split('?')[0];
      var scripts = document.getElementsByTagName('script');
      for (var i = 0; i < scripts.length; i++) {
        if (scripts[i].src && scripts[i].src.split('?')[0] === base) return;
      }
      var script = document.createElement('script');
      script.async = true;
      script.src = src;
      document.body.appendChild(script);
    }

    function onInteract() {
      if (interacted) return;
      interacted = true;
      events.forEach(function (name) { window.removeEventListener(name, onInteract, opts); });
      load();
    }

    if (!near) {
      var observer = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        near = true;
        observer.disconnect();
        load();
      }, { rootMargin: '100% 0px' });
      observer.observe(feed);
    }

    events.forEach(function (name) { window.addEventListener(name, onInteract, opts); });
  }

  // The block prints its script URL as instafeedScriptSrc (the file name
  // changes with app updates). If that name ever changes, read the URL from
  // the block's own inline script. Searched inside the block only: the head
  // also lists an old Instafeed script that no longer exists.
  function instafeedSrc(feed) {
    if (typeof window.instafeedScriptSrc === 'string' && window.instafeedScriptSrc) {
      return window.instafeedScriptSrc;
    }
    var block = feed.closest('.shopify-block') || feed.parentNode;
    var scripts = block ? block.querySelectorAll('script:not([src])') : [];
    for (var i = 0; i < scripts.length; i++) {
      var match = scripts[i].textContent.match(/https:(?:\\?\/){2}cdn\.nfcube\.com\\?\/instafeed-[\w-]+\.js[^"'\s]*/);
      if (match) return match[0].replace(/\\\//g, '/');
    }
    return '';
  }


  /* --------------------------------------------------------
     Public API namespace for section-scoped scripts to share.
     -------------------------------------------------------- */
  window.Moonrise = window.Moonrise || {};

  /* --------------------------------------------------------
     Shared overlay accessibility: inert toggling, focus move,
     focus return, and a Tab trap. Used by the mobile nav, the
     search drawer and the cart drawer.

     Why inert and not aria-hidden: aria-hidden removes an element
     from the accessibility tree but leaves its contents in the tab
     order, so a keyboard user tabs into an invisible drawer and
     focus disappears. inert does both jobs in one attribute.

     Focusables are queried on every Tab, never cached. The cart
     drawer replaces its own innerHTML on each cart update, so a
     cached list would go stale immediately.
     -------------------------------------------------------- */
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function visibleFocusables(panel) {
    return Array.prototype.filter.call(
      panel.querySelectorAll(FOCUSABLE),
      function (el) {
        /* offsetParent is null for display:none; a closed submenu inside the
           mobile nav must not be reachable. */
        return el.offsetParent !== null && !el.closest('[inert]');
      }
    );
  }

  window.Moonrise.overlay = {
    /* Called when a panel opens. trigger is the element that opened it, so
       focus can be handed back to exactly that control on close. */
    open: function (panel, trigger, preferredSelector) {
      if (!panel) return;
      panel.removeAttribute('inert');
      panel.removeAttribute('aria-hidden');
      panel._mrTrigger = trigger || document.activeElement || null;

      if (!panel._mrTrapBound) {
        panel._mrTrapBound = function (e) {
          if (e.key !== 'Tab') return;
          var items = visibleFocusables(panel);
          if (!items.length) return;
          var first = items[0];
          var last = items[items.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          } else if (!panel.contains(document.activeElement)) {
            /* Focus escaped, e.g. it was inside a node the cart re-render
               replaced. Pull it back to the start of the panel. */
            e.preventDefault();
            first.focus();
          }
        };
        panel.addEventListener('keydown', panel._mrTrapBound);
        document.addEventListener('keydown', panel._mrTrapBound);
      }

      /* The panels slide in over 0.35s; focusing mid-transition makes the
         browser scroll to a half-positioned element. */
      window.setTimeout(function () {
        var target = preferredSelector ? panel.querySelector(preferredSelector) : null;
        if (!target) target = visibleFocusables(panel)[0];
        if (!target) {
          panel.setAttribute('tabindex', '-1');
          target = panel;
        }
        try { target.focus(); } catch (err) { /* noop */ }
      }, 350);
    },

    close: function (panel) {
      if (!panel) return;
      var trigger = panel._mrTrigger;

      /* Move focus out before the panel goes inert: blurring an element that
         is already inert leaves focus on <body> in some browsers. */
      if (trigger && typeof trigger.focus === 'function' && document.body.contains(trigger)) {
        try { trigger.focus(); } catch (err) { /* noop */ }
      }
      panel._mrTrigger = null;

      if (panel._mrTrapBound) {
        panel.removeEventListener('keydown', panel._mrTrapBound);
        document.removeEventListener('keydown', panel._mrTrapBound);
        panel._mrTrapBound = null;
      }

      panel.setAttribute('inert', '');
      panel.removeAttribute('aria-hidden');
    }
  };
  window.Moonrise.openDrawer = function (which) {
    var t = document.querySelector('[data-drawer-trigger="' + which + '"]');
    if (t) t.click();
  };

  // Called by cart-drawer.js after the Section Rendering API swaps innerHTML.
  // The new DOM elements need their drawer behaviors re-bound.
  window.Moonrise.rebindDrawers = function () {
    initSiteDrawers();
  };

})();
